import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import CustomButton from "../../components/CustomButton/Button";
import styled from "styled-components/macro";
import CommonPageLayout from "../../components/commonPageLayout/commonPageLayout";
import {
  Tabs as MuiTabs,
  Tab,
  Box,
  Paper,
  Typography,
  TextField as MuiTextField,
  Checkbox as MuiCheckbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip
} from "@mui/material";
import axios from "axios";
import { settingsCategories } from "../../utils/commonConstants";
import {
  GET_BUSINESS_SETTINGS,
  UPDATE_BUSINESS_SETTINGS
} from "../../utils/url";
import Loader from "../../components/loader/Loader";
import CommonButton from "../../components/CommonButton/CommonButton";
import DefaultIcon from "../../assets/icons/default.jpg";

const TextField = styled(MuiTextField)`
  display: flex;
  padding-top: 1px;
  justify-content: center;
  align-items: center;
  label {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: 0.15px;
  }

  input {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    font-feature-settings: "clig" off, "liga" off;
    font-family: "Montserrat", sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.15px;
    &::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
    &:hover:not(.Mui-disabled)::before {
      height: 1px;
      background: rgba(0, 0, 0, 0.42);
    }
  }
`;

const Tabs = styled(MuiTabs)`
  span {
    background-color: #192f67;
  }
  button {
    color: var(--light-text-secondary, rgba(0, 0, 0, 0.6));
    text-align: center;
    font-family: "Montserrat", sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .Mui-selected {
    color: var(--light-primary-main, #192f67);
  }
`;

const SettingContainer = styled(Paper)`
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Setting {
  SettingName: string;
  Description: string;
  DefaultValue: string;
  Merchant_Overwrite_Value: string | null;
  Merchant_Overwrite_Value_Old: string | null;
  Version?: string;
  Type?: "text" | "number" | "boolean" | "select" | "email" | "profile_builder";
  Options?: string[];
  MinValue?: number;
  MaxValue?: number;
}

interface UpdatedSetting {
  SettingName: string;
  SettingValue: string | number;
}

interface ProfileBuilderConfig {
  profilescreen: string;
}

// Helpers to expand/compress profile builder combinations
const UI_ORDER = ["E", "N", "D", "M", "PC", "A"];

// Canonical mapping from selected fields to exact payload to send
// Keys are '+'-joined in UI_ORDER (e.g., 'E+N+D+M')
const PROFILE_SELECTION_PAYLOAD: Record<string, ProfileBuilderConfig[]> = {
  // Singles
  E: [{ profilescreen: "E" }],
  N: [{ profilescreen: "N" }],
  D: [{ profilescreen: "D" }],
  M: [{ profilescreen: "M" }],
  PC: [{ profilescreen: "PC" }],
  // Combinations
  "E+N+D": [{ profilescreen: "A" }],
  "E+N+D+M": [{ profilescreen: "AM" }],
  "E+PC": [{ profilescreen: "EP" }],
  "E+N+PC": [{ profilescreen: "EFSP" }],
  "E+M+PC": [{ profilescreen: "EMP" }]
};

function normalizeCodesForUI(codes: string[]): string[] {
  const result = new Set<string>();
  codes.forEach((code) => {
    switch (code) {
      case "A":
        ["E", "N", "D"].forEach((c) => result.add(c));
        break;
      case "AM":
        ["E", "N", "M", "D"].forEach((c) => result.add(c));
        break;
      case "EP":
        ["E", "PC"].forEach((c) => result.add(c));
        break;
      case "EFSP":
        ["E", "N", "PC"].forEach((c) => result.add(c));
        break;
      case "EMP":
        ["E", "M", "PC"].forEach((c) => result.add(c));
        break;
      default:
        if (UI_ORDER.includes(code)) {
          result.add(code);
        }
    }
  });
  return Array.from(result).sort(
    (a, b) => UI_ORDER.indexOf(a) - UI_ORDER.indexOf(b)
  );
}

function expandProfileConfigForUI(
  arr: ProfileBuilderConfig[]
): ProfileBuilderConfig[] {
  const inputCodes = arr.map((a) => a.profilescreen);
  let expanded = normalizeCodesForUI(inputCodes);

  // If all individuals are present, ensure 'A' is also shown as selected
  const hasAllIndividuals = ["E", "N", "D", "M", "PC"].every((f) =>
    expanded.includes(f)
  );
  if (hasAllIndividuals && !expanded.includes("A")) {
    expanded = [...expanded, "A"].sort(
      (a, b) => UI_ORDER.indexOf(a) - UI_ORDER.indexOf(b)
    );
  }

  return expanded.map((c) => ({ profilescreen: c }));
}

function buildProfilePayloadFromUI(selected: string[]): ProfileBuilderConfig[] {
  const individuals = selected.filter((c) =>
    ["E", "N", "D", "M", "PC"].includes(c)
  );

  // If all five individuals are selected, send AM
  const hasAllIndividuals = ["E", "N", "D", "M", "PC"].every((f) =>
    individuals.includes(f)
  );
  if (hasAllIndividuals) {
    return [{ profilescreen: "AM" }, { profilescreen: "PC" }];
  }

  // If A is selected (and not all individuals), send A
  if (selected.includes("A")) {
    return [{ profilescreen: "A" }];
  }

  // If E+N+D selected with PC, send A plus PC
  const hasEND = ["E", "N", "D"].every((f) => individuals.includes(f));
  if (hasEND && individuals.includes("PC") && !individuals.includes("M")) {
    return [{ profilescreen: "A" }, { profilescreen: "PC" }];
  }

  const key = [...individuals]
    .sort((a, b) => UI_ORDER.indexOf(a) - UI_ORDER.indexOf(b))
    .join("+");
  const mapped = PROFILE_SELECTION_PAYLOAD[key];
  if (mapped) {
    return mapped;
  }
  if (individuals.length === 1) {
    return [{ profilescreen: individuals[0] }];
  }
  const ordered = individuals.sort(
    (a, b) => UI_ORDER.indexOf(a) - UI_ORDER.indexOf(b)
  );
  return ordered.map((c) => ({ profilescreen: c }));
}

const StyledCheckbox = styled(MuiCheckbox)`
  &.Mui-checked {
    color: #192f67;
  }
  &.MuiCheckbox-root {
    color: #192f67;
  }
`;

const SettingsPage = () => {
  const { businessId, merchantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const hasInitialized = useRef(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [settings, setSettings] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [updatedSettings, setUpdatedSettings] = useState<UpdatedSetting[]>([]);
  const [notificationMessage, setNotificationMessage] = useState({
    message: "",
    type: ""
  });
  console.log("🚀 ~ SettingsPage ~ updatedSettings:", updatedSettings);
  const [, setVersion] = useState<string>("");
  const [profileBuilderConfig, setProfileBuilderConfig] = useState<
    ProfileBuilderConfig[]
  >([]);
  const [scansBeforePB, setScansBeforePB] = useState<string>("");

  const handleRedirectToBusinessSearchPage = () => {
    localStorage.setItem("isBackToBusinessSearchPage", "true");
    navigate("/business-search");
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setUpdatedSettings([]);
    setSelectedTab(newValue);
    // Remove tab query param once user changes tab so URL stays clean
    const params = new URLSearchParams(location.search);
    if (params.has("tab")) {
      params.delete("tab");
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : ""
        },
        { replace: true }
      );
    }
    fetchSettings(settingsCategories[newValue]);
  };

  const fetchSettings = useCallback(
    async (category: string) => {
      if (!businessId) return;

      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}${GET_BUSINESS_SETTINGS(
            businessId,
            category
          )}`,
          {
            headers: {
              Authorization: `bearer ${localStorage.getItem("accessToken")}`
            }
          }
        );
        setSettings(response.data);

        const versionSetting = response.data.find(
          (s: Setting) => s.SettingName === "VERSION"
        );
        if (versionSetting) {
          setVersion(
            versionSetting.Merchant_Overwrite_Value ||
              versionSetting.DefaultValue
          );
        }

        // Handle profile builder settings
        if (category === "profile") {
          const profileBuilderSetting = response.data.find(
            (s: Setting) => s.SettingName === "PROFILE_BUILDER_CONFIG"
          );
          if (profileBuilderSetting) {
            const value =
              profileBuilderSetting.Merchant_Overwrite_Value ||
              profileBuilderSetting.DefaultValue;
            try {
              const config = JSON.parse(value);
              setProfileBuilderConfig(expandProfileConfigForUI(config));
            } catch (e) {
              console.error("Error parsing profile builder config:", e);
              setProfileBuilderConfig([]);
            }
          }

          // Fetch SCANSBEFOREPB setting separately
          try {
            const scansResponse = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}${GET_BUSINESS_SETTINGS(
                businessId,
                "scanbeforepb"
              )}`,
              {
                headers: {
                  Authorization: `bearer ${localStorage.getItem("accessToken")}`
                }
              }
            );

            const scansBeforePBSetting = scansResponse.data.find(
              (s: Setting) => s.SettingName === "SCANSBEFOREPB"
            );
            if (scansBeforePBSetting) {
              const value =
                scansBeforePBSetting.Merchant_Overwrite_Value ||
                scansBeforePBSetting.DefaultValue;
              setScansBeforePB(value ?? "");
            }
          } catch (scansErr) {
            console.error("Error fetching SCANSBEFOREPB setting:", scansErr);
            setScansBeforePB("");
          }
        }
      } catch (err) {
        setError("Failed to fetch settings");
        setNotificationMessage({
          message: "Failed to fetch settings",
          type: "error"
        });
        window.scroll({
          top: 0,
          left: 0,
          behavior: "smooth"
        });
        console.error("Error fetching settings:", err);
      } finally {
        setLoading(false);
      }
    },
    [businessId]
  );

  const handleSettingChange = (
    settingName: string,
    newValue: string | boolean,
    existingIndex: number,
    name: string
  ) => {
    const stringValue =
      typeof newValue === "boolean" ? newValue.toString() : newValue || null;

    // First update the settings state
    setSettings((prev: any) => {
      const newSettings = [...prev];
      newSettings[existingIndex] = {
        ...newSettings[existingIndex],
        [name]: stringValue
      };
      return newSettings;
    });

    // Then update the updatedSettings state
    setUpdatedSettings((prev: any) => {
      const existingSettingIndex = prev.findIndex(
        (setting: any) => setting.SettingName === settingName
      );

      if (existingSettingIndex >= 0) {
        const newSettings = [...prev];
        newSettings[existingSettingIndex] = {
          SettingName: settingName,
          SettingValue: stringValue
        };
        return newSettings;
      }

      return [
        ...prev,
        {
          SettingName: settingName,
          SettingValue: stringValue
        }
      ];
    });
  };

  const handleSaveSettings = async () => {
    if (!merchantId || !businessId || updatedSettings.length === 0) return;

    setSaveLoading(true);
    try {
      // Ensure SCANSBEFOREPB is sent as an integer (default to 0 when empty)
      const normalizedSettings = updatedSettings.map((s) => {
        if (s.SettingName === "SCANSBEFOREPB") {
          const numericValue =
            typeof s.SettingValue === "number"
              ? s.SettingValue
              : Number((s.SettingValue as string) || 0);
          return { ...s, SettingValue: numericValue };
        }
        return s;
      });

      const data = {
        MerchantId: merchantId,
        Settings: normalizedSettings
      };

      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}${UPDATE_BUSINESS_SETTINGS(
          businessId
        )}`,
        data,
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );

      // Clear updated settings and refresh current category
      setUpdatedSettings([]);
      setNotificationMessage({
        message: "Settings updated successfully",
        type: "success"
      });
      window.scroll({
        top: 0,
        left: 0,
        behavior: "smooth"
      });
      fetchSettings(settingsCategories[selectedTab]);
    } catch (err) {
      console.error("Error updating settings:", err);
      setError("Failed to update settings");
      setNotificationMessage({
        message: "Failed to update settings",
        type: "error"
      });
      window.scroll({
        top: 0,
        left: 0,
        behavior: "smooth"
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleScansBeforePBChange = (rawValue: string) => {
    // keep only digits
    const sanitized = rawValue.replace(/\D+/g, "");
    setScansBeforePB(sanitized);

    // Add to updatedSettings for saving
    setUpdatedSettings((prev: any) => {
      const existingSettingIndex = prev.findIndex(
        (setting: any) => setting.SettingName === "SCANSBEFOREPB"
      );

      if (existingSettingIndex >= 0) {
        const newSettings = [...prev];
        newSettings[existingSettingIndex] = {
          SettingName: "SCANSBEFOREPB",
          SettingValue: sanitized === "" ? 0 : Number(sanitized)
        };
        return newSettings;
      }

      return [
        ...prev,
        {
          SettingName: "SCANSBEFOREPB",
          SettingValue: sanitized === "" ? 0 : Number(sanitized)
        }
      ];
    });
  };

  const handleProfileBuilderToggle = (checked: boolean, screenType: string) => {
    let newConfig: ProfileBuilderConfig[] = [...profileBuilderConfig];

    if (checked) {
      // Add the screen type if not already present
      if (!newConfig.some((config) => config.profilescreen === screenType)) {
        newConfig.push({ profilescreen: screenType });
      }

      // Handle mutual exclusivity rules
      if (screenType === "A") {
        // If "All fields" is selected, auto-select all individual fields
        const allIndividualFields = ["E", "N", "D", "M", "PC"];
        allIndividualFields.forEach((field) => {
          if (!newConfig.some((config) => config.profilescreen === field)) {
            newConfig.push({ profilescreen: field });
          }
        });
      } else {
        // If any individual field is selected, remove "All fields" if it exists
        newConfig = newConfig.filter((config) => config.profilescreen !== "A");
      }
    } else {
      // Remove the screen type if present
      newConfig = newConfig.filter(
        (config) => config.profilescreen !== screenType
      );

      // If "All fields" is being unchecked, remove all individual fields
      if (screenType === "A") {
        newConfig = newConfig.filter(
          (config) => !["E", "N", "D", "M", "PC"].includes(config.profilescreen)
        );
      } else {
        // If any individual field is unchecked, remove "All fields" if it exists
        newConfig = newConfig.filter((config) => config.profilescreen !== "A");
      }
    }

    // If all individuals are selected, ensure A is checked in UI
    const allIndividualFields = ["E", "N", "D", "M", "PC"];
    const allSelected = allIndividualFields.every((f) =>
      newConfig.some((c) => c.profilescreen === f)
    );
    if (allSelected && !newConfig.some((c) => c.profilescreen === "A")) {
      newConfig.push({ profilescreen: "A" });
    }

    // Sort the config based on the order: E, N, D, M, PC, A
    const order = ["E", "N", "D", "M", "PC", "A"];
    newConfig.sort(
      (a, b) => order.indexOf(a.profilescreen) - order.indexOf(b.profilescreen)
    );

    setProfileBuilderConfig(newConfig);

    // Update pending settings payload directly
    const payloadArray = buildProfilePayloadFromUI(
      newConfig.map((c) => c.profilescreen)
    );
    const stringValue = JSON.stringify(payloadArray);
    setUpdatedSettings((prev: any) => {
      const existingSettingIndex = prev.findIndex(
        (s: any) => s.SettingName === "PROFILE_BUILDER_CONFIG"
      );

      if (existingSettingIndex >= 0) {
        const next = [...prev];
        next[existingSettingIndex] = {
          SettingName: "PROFILE_BUILDER_CONFIG",
          SettingValue: stringValue
        };
        return next;
      }

      return [
        ...prev,
        { SettingName: "PROFILE_BUILDER_CONFIG", SettingValue: stringValue }
      ];
    });
  };

  const renderProfileTabContent = () => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* PROFILE_BUILDER_CONFIG Setting */}
        <SettingContainer>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Profile Builder Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure which profile fields to collect from users. Select the
            combination of fields you want to gather.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControlLabel
              control={
                <StyledCheckbox
                  checked={profileBuilderConfig.some(
                    (c) => c.profilescreen === "E"
                  )}
                  onChange={(e) =>
                    handleProfileBuilderToggle(e.target.checked, "E")
                  }
                />
              }
              label="Email"
            />
            <FormControlLabel
              control={
                <StyledCheckbox
                  checked={profileBuilderConfig.some(
                    (c) => c.profilescreen === "N"
                  )}
                  onChange={(e) =>
                    handleProfileBuilderToggle(e.target.checked, "N")
                  }
                />
              }
              label="Firstname and Surname"
            />
            <FormControlLabel
              control={
                <StyledCheckbox
                  checked={profileBuilderConfig.some(
                    (c) => c.profilescreen === "D"
                  )}
                  onChange={(e) =>
                    handleProfileBuilderToggle(e.target.checked, "D")
                  }
                />
              }
              label="Date of Birth"
            />
            <FormControlLabel
              control={
                <StyledCheckbox
                  checked={profileBuilderConfig.some(
                    (c) => c.profilescreen === "M"
                  )}
                  onChange={(e) =>
                    handleProfileBuilderToggle(e.target.checked, "M")
                  }
                />
              }
              label="Mobile"
            />
            <FormControlLabel
              control={
                <StyledCheckbox
                  checked={profileBuilderConfig.some(
                    (c) => c.profilescreen === "PC"
                  )}
                  onChange={(e) =>
                    handleProfileBuilderToggle(e.target.checked, "PC")
                  }
                />
              }
              label="Postcode"
            />
            <FormControlLabel
              control={
                <StyledCheckbox
                  checked={profileBuilderConfig.some(
                    (c) => c.profilescreen === "A"
                  )}
                  onChange={(e) =>
                    handleProfileBuilderToggle(e.target.checked, "A")
                  }
                />
              }
              label="All fields on one screen"
            />
          </Box>
        </SettingContainer>

        {/* SCANSBEFOREPB Setting */}
        <SettingContainer>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Scans Before Profile Builder
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Number of scans required before showing the profile builder to
            users.
          </Typography>
          <TextField
            fullWidth
            id="scansBeforePB"
            label="Number of Scans"
            value={scansBeforePB}
            onChange={(e) => handleScansBeforePBChange(e.target.value)}
            variant="outlined"
            placeholder="Enter number of scans"
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*", min: 0 }}
          />
        </SettingContainer>
      </Box>
    );
  };

  const renderSettingInput = (setting: Setting, index: number) => {
    const value = setting.Merchant_Overwrite_Value || setting.DefaultValue;
    let name = setting.Merchant_Overwrite_Value
      ? "Merchant_Overwrite_Value"
      : "DefaultValue";

    // Handle number type settings
    const isValidNumber = () => {
      const numericValue: any = !value || value === "" ? "" : Number(value);
      const isNumber = !isNaN(numericValue) && value !== "";
      return isNumber;
    };

    if (value === "true" || value === "false") {
      return (
        <FormControlLabel
          control={
            <StyledCheckbox
              checked={value === "true"}
              onChange={(e) =>
                handleSettingChange(
                  setting.SettingName,
                  e.target.checked,
                  index,
                  name
                )
              }
            />
          }
          label=""
        />
      );
    } else if (isValidNumber()) {
      return (
        <TextField
          id={setting.SettingName}
          label=""
          type="number"
          variant="standard"
          size="small"
          value={value}
          onChange={(e: any) => {
            const newValue = e.target.value;
            // Allow empty string or valid numbers
            handleSettingChange(setting.SettingName, newValue, index, name);
          }}
        />
      );
    } else {
      return (
        <TextField
          id={setting.SettingName}
          label=""
          type="text"
          variant="standard"
          fullWidth
          value={value}
          onChange={(e: any) =>
            handleSettingChange(
              setting.SettingName,
              e.target.value,
              index,
              name
            )
          }
        />
      );
    }
  };

  useEffect(() => {
    if (hasInitialized.current || !businessId) return;

    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get("tab");

    let initialIndex = 0;
    if (tabParam) {
      const parsed = Number(tabParam);
      const candidateIndex = parsed - 1;
      if (
        !Number.isNaN(candidateIndex) &&
        candidateIndex >= 0 &&
        candidateIndex < settingsCategories.length
      ) {
        initialIndex = candidateIndex;
      }
    }

    hasInitialized.current = true;
    setSelectedTab(initialIndex);
    fetchSettings(settingsCategories[initialIndex]);
  }, [businessId, location.search, fetchSettings]);

  return (
    <CommonPageLayout
      heading="Business Settings"
      subHeading={`Manage Business Settings`}
      docs={
        <CustomButton callBack={handleRedirectToBusinessSearchPage}>
          {"Back"}
        </CustomButton>
      }
    >
      <Paper elevation={0} style={{ padding: "20px" }}>
        {notificationMessage.message && (
          <Alert
            severity={notificationMessage.type as any}
            sx={{ mb: 2 }}
            onClose={() => setNotificationMessage({ message: "", type: "" })}
          >
            {notificationMessage.message}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {settingsCategories.map((category, index) => (
              <Tab
                key={category}
                label={
                  category === "profile"
                    ? "Profile Builder"
                    : category.charAt(0).toUpperCase() + category.slice(1)
                }
                id={`settings-tab-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        {settingsCategories.map((category, index) => (
          <TabPanel key={category} value={selectedTab} index={index}>
            {loading ? (
              <Loader />
            ) : category === "profile" ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {renderProfileTabContent()}
                <Box sx={{ mt: 2, display: "flex" }}>
                  <CommonButton
                    onClick={() =>
                      updatedSettings?.length > 0
                        ? handleSaveSettings()
                        : () => {}
                    }
                    disabled={saveLoading || updatedSettings?.length <= 0}
                    variant="contained"
                  >
                    {saveLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Save"
                    )}
                  </CommonButton>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {settings?.map((setting: any, index: any) => {
                  const value =
                    setting.Merchant_Overwrite_Value || setting.DefaultValue;
                  return (
                    <SettingContainer key={setting.SettingName}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {(value === "true" || value === "false") &&
                          renderSettingInput(setting, index)}
                        <Typography variant="h6">
                          {setting.SettingName?.split("_")?.join(" ")}
                        </Typography>
                        {!setting.Merchant_Overwrite_Value && (
                          <Tooltip title="Default Value">
                            <img
                              src={DefaultIcon}
                              alt="DefaultValue"
                              style={{
                                cursor: "pointer",
                                width: "25px",
                                height: "25px"
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {setting.Description}
                      </Typography>

                      {value !== "true" && value !== "false" && (
                        <Box
                          sx={{ display: "flex", gap: 2, alignItems: "center" }}
                        >
                          {renderSettingInput(setting, index)}
                        </Box>
                      )}
                    </SettingContainer>
                  );
                })}

                <Box sx={{ mt: 2, display: "flex" }}>
                  <CommonButton
                    onClick={() =>
                      updatedSettings?.length > 0
                        ? handleSaveSettings()
                        : () => {}
                    }
                    disabled={saveLoading || updatedSettings?.length <= 0}
                    variant="contained"
                  >
                    {saveLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Save"
                    )}
                  </CommonButton>
                </Box>
              </Box>
            )}
          </TabPanel>
        ))}
      </Paper>
    </CommonPageLayout>
  );
};

export default SettingsPage;
